/**
 * workingHoursHelper.js
 *
 * Centralizes all business-hours arithmetic used across the app
 * (EntregaModal, AutoCloseInfo, etc.).
 *
 * The schedule format is the canonical one from `config.work_schedule`:
 *   [{ day: 1, start_working_hour: "08:00", end_working_hour: "17:00" }, …]
 * where `day` follows isoWeekday() convention (1 = Mon … 7 = Sun).
 * Only days present in the array are considered working days.
 */

import moment from 'moment'

// ─── internal helpers ────────────────────────────────────────────────────────

/**
 * Build a fast lookup map from a work_schedule array.
 * @param {Array} schedule
 * @returns {Object} { [isoWeekday]: scheduleEntry }
 */
function buildScheduleMap(schedule) {
  const map = {}
  ;(schedule || []).forEach(s => { map[s.day] = s })
  return map
}

/**
 * Parse "HH:MM" into { hours, minutes }.
 */
function parseHHMM(str) {
  const parts = (str || '00:00').split(':')
  return { hours: parseInt(parts[0], 10), minutes: parseInt(parts[1], 10) }
}

/**
 * Given a moment cursor and a schedule entry, return the working window
 * for that calendar day as { dayStart: moment, dayEnd: moment }.
 */
function getDayWindow(cursor, dayConfig) {
  const s = parseHHMM(dayConfig.start_working_hour)
  const e = parseHHMM(dayConfig.end_working_hour)

  const dayStart = cursor.clone().startOf('day').add(s.hours, 'hours').add(s.minutes, 'minutes')
  const dayEnd   = cursor.clone().startOf('day').add(e.hours, 'hours').add(e.minutes, 'minutes')

  return { dayStart, dayEnd }
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Count the number of working hours between two moments,
 * respecting the per-day schedule.
 *
 * Equivalent to the old `calcWorkingHours` in AutoCloseInfo.
 *
 * @param {moment.Moment|Date|string} start
 * @param {moment.Moment|Date|string} end
 * @param {Array} schedule  – config.work_schedule
 * @returns {number} fractional hours
 */
export function calcWorkingHours(start, end, schedule) {
  const scheduleMap = buildScheduleMap(schedule)
  let cursor = moment(start).clone()
  const endMoment = moment(end)
  let hours = 0

  while (cursor.isBefore(endMoment)) {
    const dow = cursor.isoWeekday()
    const dayConfig = scheduleMap[dow]

    if (dayConfig) {
      const { dayStart, dayEnd } = getDayWindow(cursor, dayConfig)
      const from = moment.max(cursor, dayStart)
      const to   = moment.min(endMoment, dayEnd)

      if (to.isAfter(from)) {
        hours += to.diff(from, 'minutes') / 60
      }
    }

    cursor.add(1, 'day').startOf('day')
  }

  return hours
}

/**
 * Starting from `start`, advance exactly `hoursToAdd` working hours
 * and return the resulting moment.
 *
 * Equivalent to the old `addWorkingHours` in AutoCloseInfo.
 *
 * @param {moment.Moment|Date|string} start
 * @param {number} hoursToAdd
 * @param {Array} schedule  – config.work_schedule
 * @returns {moment.Moment}
 */
export function addWorkingHours(start, hoursToAdd, schedule) {
  const scheduleMap = buildScheduleMap(schedule)
  let cursor = moment(start).clone()
  let remaining = hoursToAdd

  for (let i = 0; i < 365 && remaining > 0; i++) {
    const dow = cursor.isoWeekday()
    const dayConfig = scheduleMap[dow]

    if (dayConfig) {
      const { dayStart, dayEnd } = getDayWindow(cursor, dayConfig)
      const from = moment.max(cursor, dayStart)

      if (from.isBefore(dayEnd)) {
        const avail = dayEnd.diff(from, 'minutes') / 60

        if (remaining <= avail) {
          return from.clone().add(remaining * 60, 'minutes')
        }

        remaining -= avail
      }
    }

    cursor.add(1, 'day').startOf('day')
  }

  return cursor
}

/**
 * Return true if `now` falls within the working window of its calendar day.
 *
 * @param {moment.Moment|Date|string} now
 * @param {Array} schedule  – config.work_schedule
 * @returns {boolean}
 */
export function isWithinWorkingHours(now, schedule) {
  const scheduleMap = buildScheduleMap(schedule)
  const nowMoment = moment(now)
  const dow = nowMoment.isoWeekday()
  const dayConfig = scheduleMap[dow]

  if (!dayConfig) return false

  const { dayStart, dayEnd } = getDayWindow(nowMoment, dayConfig)
  return nowMoment.isBetween(dayStart, dayEnd)
}

/**
 * Return a moment pointing to the start of the next working period
 * (could be later today if we are before today's window, or the next
 * working day otherwise).
 *
 * @param {moment.Moment|Date|string} now
 * @param {Array} schedule  – config.work_schedule
 * @returns {moment.Moment}
 */
export function nextWorkingPeriodStart(now, schedule) {
  const scheduleMap = buildScheduleMap(schedule)
  let cursor = moment(now).clone()

  for (let i = 0; i < 14; i++) {
    const dow = cursor.isoWeekday()
    const dayConfig = scheduleMap[dow]

    if (dayConfig) {
      const { dayStart, dayEnd } = getDayWindow(cursor, dayConfig)

      // Still before the working window starts today → return dayStart
      if (cursor.isBefore(dayStart)) return dayStart

      // Inside the window → caller is already in a working period;
      // advance to next day to find the *next* start
      if (cursor.isBefore(dayEnd)) {
        cursor.add(1, 'day').startOf('day')
        continue
      }
    }

    // Day is not a working day, or cursor is past today's window → next day
    cursor.add(1, 'day').startOf('day')
  }

  return cursor
}

/**
 * Calculate the absolute deadline moment by adding `hoursToAdd` working
 * hours to `startTime`, using the work_schedule.
 *
 * Drop-in replacement for EntregaModal's `calculateBusinessHoursDeadline`.
 *
 * @param {Date|string} startTime
 * @param {number}      hoursToAdd
 * @param {Array}       schedule   – config.work_schedule
 * @returns {moment.Moment}
 */
export function calculateBusinessHoursDeadline(startTime, hoursToAdd, schedule) {
  return addWorkingHours(startTime, hoursToAdd, schedule)
}

/**
 * Calculate the remaining working milliseconds between `currentTime`
 * and `deadlineTime`, respecting the work_schedule.
 *
 * Drop-in replacement for EntregaModal's `calculateRemainingBusinessTime`.
 *
 * @param {Date|string} currentTime
 * @param {Date|string} deadlineTime
 * @param {Array}       schedule   – config.work_schedule
 * @returns {number} milliseconds (0 if already past deadline)
 */
export function calculateRemainingBusinessTime(currentTime, deadlineTime, schedule) {
  if (moment(currentTime).isSameOrAfter(deadlineTime)) return 0
  const hours = calcWorkingHours(currentTime, deadlineTime, schedule)
  return Math.round(hours * 60 * 60 * 1000)
}

export default {
  calcWorkingHours,
  addWorkingHours,
  isWithinWorkingHours,
  nextWorkingPeriodStart,
  calculateBusinessHoursDeadline,
  calculateRemainingBusinessTime,
}